import { Deserializer } from "jsonapi-serializer";
import { genericError } from "../models/GenericError";
import SystemStat, { Count, Total } from "../models/SystemStat";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { TranslationKeys } from "../contexts/TranslationKeys";
import {BAD_REQUEST, OK} from "../constants/response-status-codes";
import { BuildDetail } from "../models/BuildDetails";
import { NETWORK_MODE } from "../constants/network-constants";
import { USAGE } from "../constants/license-constants";
import {Translator} from "../contexts/translator";
import { LicenseAttribute } from "@/models/LicenseAttribute";

const apiConnector = new APIConnector();

export const getInstallerDownloadUrl = async(downloadTarget: string, downloadName: string): Promise<string> => {
    try {
        const url = ''
        const headers = {
            type: 'installerDownload',
            downloadTarget: downloadTarget
        }

        const response = await apiConnector.fetchData(url, true, headers);
        if (response.status === OK) {
            const blob = await response.blob();
            const downloadUrl  = URL.createObjectURL(blob);
            return new Promise<string>(resolve => {
                resolve(downloadUrl);
            })
        }
        else {
            const json = await response.json();
            return new Promise<string>((resolve, reject    ) => {
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }

                return reject({ status: response.status, message: json.message ?? genericError });
            })
        }
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}


export const getInitialSetupDetails = async (translator: Translator): Promise<{ success: boolean, buildDetail: BuildDetail }> => {
    try {
        const url = "initBuildDetails";
        let response;

        const newRecord = {
            "data": {
                "id": 1,
                "type": "initBuildDetails",
                "attributes": {
                    "version": "",
                }
            }
        }

        await apiConnector.sendDataNoAuth(url, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });
        const content = await response.json();

        return new Promise<any>(async (resolve, reject) => {
            if (response) {
                new Deserializer().deserialize(content, (error, data) => {
                    if (error) {
                        return reject({ status: response.status, id: 1, success: false, message: translator.byKey(TranslationKeys.connectionLostToServer) });
                    }

                    const buildDetail: BuildDetail = {
                        id: data['id'],
                        version: 'v' + data['version'],
                        usage: data['usage'],
                        languageCode: data['language-code'],
                        isRunningWithReversedMifare: data['running-with-reversed-mifare'],
                        authProviderType: data['auth-provider-type'],
                        isEnforceDefPwdValidation: data['enforce-def-pwd-validation'],
                        showTabCounts: data['show-tab-counts']
                    }
                    return resolve({ success: true, buildDetail });
                })
                return;
            }

            return reject({ status: response.status, success: false, message: translator.byKey(TranslationKeys.connectionLostToServer) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBuildDetails = async (): Promise<{ success: boolean, buildDetail: BuildDetail }> => {
    try {
        const url = "buildDetails";
        let response;

        const newRecord = {
            "data": {
                "id": 1,
                "type": "buildDetails",
                "attributes": {
                    "version": "",
                    "licenseShortHand": ""
                }
            }
        }

        await apiConnector.sendData(url, true, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });


        return new Promise<any>(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }

                new Deserializer().deserialize(content, (error, data) => {
                    if (error) {
                        return reject({ status: response.status, id: 1, success: false, message: genericError });
                    }

                    const highestLimitsLicensed: LicenseAttribute[] = Object.entries(data['highest-limits-licensed'] as Record<string, string>).map(([attributeId, licenseLimit]) => {
                        const parts = licenseLimit.split(":");
                        const numericLimit = parts.length > 1 ? Number(parts[1].trim()) : Number(licenseLimit);

                        return {
                            attributeId: Number(attributeId),
                            licenseLimit: numericLimit
                        };
                    });

                    const stringLowestLicenseAttributes = Object.values(data['lowest-limits-licensed']) as any;
                    const stringHighestLicenseAttributes = Object.values(data['highest-limits-licensed']) as any;
                    const stringFeaturesLicensed = Object.values(data['features-licensed']) as any;
                    const licenseStatus = data['license-status'];
                    const combinedLicenseIds = [...Object.keys(data['lowest-limits-licensed']), ...Object.keys(data['highest-limits-licensed']), ...Object.keys(data['features-licensed'])];
                    const buildDetail: BuildDetail = {
                        id: data['id'],
                        licenseShortHand: data['license-short-hand'],
                        licenseFullName: data['license-full-name'],
                        version: data['version'],
                        companyMotto: data['company-motto'],
                        companyName: data['company-name'],
                        companyWebsite: data['company-website'],
                        lowestLimitsLicensed: stringLowestLicenseAttributes,
                        highestLimitsLicensed: stringHighestLicenseAttributes,
                        highestLimitsLicensedAttributes: highestLimitsLicensed,
                        featuresLicensed: stringFeaturesLicensed,
                        appNameFull: data['app-name-full'],
                        buildQuality: data['build-quality'],
                        licenceIds: combinedLicenseIds,
                        serverAddress: data['server-address'],
                        unsecurePort: data['unsecure-port'],
                        securePort: data['secure-port'],
                        licenseStatus: licenseStatus?.['status'],
                        licenseVersion: licenseStatus?.['version'],
                        licenseDate: licenseStatus?.['date'],
                        licenseName: licenseStatus?.['license'],
                        usage: data['usage'] ?? USAGE.ON_PREM,
                        tlsMode: data['tls-mode'],
                        authProviderType: data['auth-provider-type'],
                        languageCode: data['language-code'],
                        isCloudOrLanEnabled: data['tls-mode'] !== NETWORK_MODE.NONE,
                        isRunningWithReversedMifare: data['running-with-reversed-mifare'],
                        isEnforceDefPwdValidation: data['enforce-def-pwd-validation'],
                        showTabCounts: data['show-tab-counts']
                    }
                    return resolve({ success: true, buildDetail });
                })
                return;
            }

            return reject({ status: response.status, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getSystemStats = async (statType: string, siteId?: string): Promise<SystemStat> => {
    try {
        const url = '';
        const headers = {
            type: "systemStats",
            statType: statType,
            filterBySiteId: siteId ?? ""
        }
        const response = await apiConnector.fetchData(url, true, headers);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: genericError });
            }

            const totals: Total = {
                countAll: json['totals']['countAll'] ?? 0,
                counts: json['totals']['counts'] ? json['totals']['counts'].map((count): Count => {
                    return {
                        category: count['category'],
                        count: count['count'],
                        optCategoryId: count['optCategoryid']
                    }
                }) : []
            }

            const systemStats: SystemStat = {
                type: json['attributes']['statType'],
                totals: totals
            }

            return resolve(systemStats);
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const importSoftwareLicense = async (licenseBytes, translate: any): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "importSoftwareLicense",
            attributes: {
                file: {
                    fileContentBytes: licenseBytes
                }
            }
        }

        let response;

        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }
                else if (response.status === BAD_REQUEST) {
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.failedToReadLicense) })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const verifyFileLocation = async (path: string): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "fileLocationVerify",
            attributes: {
                file: {
                    fileName: path
                }
            }
        }

        let response;

        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    const content = await response.json();
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) ?? genericError })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
